import React, { Fragment, useState, useEffect } from "react";

import Loader from "../Loader";
import Profile from "./Profile";

import DatePicker from "react-date-picker";
import { addDays } from "date-fns";

import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

import { useAlert } from "react-alert";
import { useDispatch, useSelector } from "react-redux";

import { updateProfile, clearErrors } from "../../actions/userActions";
import { loadUser } from "../../actions/authActions";
import { UPDATE_PROFILE_RESET } from "../../constants/userConstants";

import nationalityData from "./../../nationality.json";
import { getDisplayEmail } from "../../utils/userDisplay";
import "./EditProfile.css";

const splitFullName = (value) => {
  const trimmed = value.trim();
  if (!trimmed) {
    return { first_name: "", last_name: "" };
  }

  const parts = trimmed.split(/\s+/);
  return {
    first_name: parts[0] || "",
    last_name: parts.slice(1).join(" ") || "",
  };
};

const EditProfile = ({ history }) => {
  const alert = useAlert();
  const dispatch = useDispatch();

  const [fullName, setFullName] = useState("");
  const [mobileNumber, setMobileNumber] = useState("");
  const [mobileCode, setMobileCode] = useState("");
  const [nationality, setNationality] = useState("");
  const [gender, setGender] = useState("");
  const [email, setEmail] = useState("");
  const [dobValue, setDobValue] = useState(null);
  const [errorData, setErrorData] = useState({});

  const { user } = useSelector((state) => state.auth);
  const { loading, isUpdated, error, errors } = useSelector((state) => state.user);

  const closeModal = () => {
    history.push("/profile");
  };

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        history.push("/profile");
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [history]);

  useEffect(() => {
    if (!user) return;

    setFullName([user.first_name, user.last_name].filter(Boolean).join(" "));
    // Keep internal @school.ots placeholders out of edits; only send a real email if present.
    setEmail(getDisplayEmail(user.email));
    setMobileCode(user.mobile_code || "");
    setMobileNumber(user.mobile_number || "");
    setNationality(user.nationality || "");
    setGender(user.gender || "");
    setDobValue(user.dob ? new Date(user.dob) : null);
  }, [user]);

  useEffect(() => {
    if (errors) {
      setErrorData(errors);
    }

    if (error) {
      alert.error(error);
      dispatch(clearErrors());
    }

    if (isUpdated) {
      alert.success("Profile updated successfully");
      dispatch(loadUser());
      closeModal();
      dispatch({ type: UPDATE_PROFILE_RESET });
    }
  }, [dispatch, isUpdated, alert, error, errors, history]);

  const phoneInputValue =
    mobileCode && mobileNumber ? `${mobileCode}${mobileNumber}` : mobileCode || mobileNumber || "";

  const onPhoneChange = (value, country) => {
    const dialCode = country?.dialCode || "";
    setMobileCode(dialCode);
    setMobileNumber(value.slice(dialCode.length));
  };

  const submitHandler = (e) => {
    e.preventDefault();
    const { first_name, last_name } = splitFullName(fullName);
    // Prefer a real email the student entered; otherwise keep the existing account email.
    const emailToSave = getDisplayEmail(email) || user?.email || "";

    dispatch(
      updateProfile(
        first_name,
        last_name,
        mobileCode,
        mobileNumber,
        emailToSave,
        dobValue,
        gender,
        nationality
      )
    );
  };

  return (
    <div className="edit-profile-route">
      <div className="edit-profile-route__backdrop" aria-hidden="true">
        <Profile history={history} />
      </div>

      <div
        className="edit-profile-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="editProfileTitle"
        onClick={closeModal}
      >
        <div className="edit-profile-modal__dialog" onClick={(event) => event.stopPropagation()}>
          {loading ? (
            <Loader />
          ) : (
            <Fragment>
              <div className="edit-profile-modal__header">
                <h2 id="editProfileTitle" className="edit-profile-modal__title">
                  Edit Profile
                </h2>
                <button
                  type="button"
                  className="edit-profile-modal__close"
                  onClick={closeModal}
                  aria-label="Close edit profile"
                >
                  <i className="fas fa-times" aria-hidden="true" />
                </button>
              </div>

              <form className="edit-profile-modal__form" onSubmit={submitHandler}>
                <div className="edit-profile-modal__field">
                  <label htmlFor="fullName" className="edit-profile-modal__label">
                    Full Name
                  </label>
                  <input
                    type="text"
                    id="fullName"
                    name="fullName"
                    className="edit-profile-modal__input"
                    placeholder="Enter full name"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                  />
                  {errorData.first_name || errorData.last_name ? (
                    <span className="edit-profile-modal__error">
                      {errorData.first_name || errorData.last_name}
                    </span>
                  ) : null}
                </div>

                <div className="edit-profile-modal__field edit-profile-modal__phone">
                  <label htmlFor="phone" className="edit-profile-modal__label">
                    Phone
                  </label>
                  <PhoneInput
                    inputProps={{
                      name: "phone",
                      id: "phone",
                      required: true,
                    }}
                    value={phoneInputValue}
                    onChange={onPhoneChange}
                    enableSearch
                    country="ng"
                    placeholder="+234 7044268866"
                  />
                  {errorData.mobile_code || errorData.mobile_number ? (
                    <span className="edit-profile-modal__error">
                      {errorData.mobile_code || errorData.mobile_number}
                    </span>
                  ) : null}
                </div>

                <div className="edit-profile-modal__field-row">
                  <div>
                    <label htmlFor="nationality" className="edit-profile-modal__label">
                      Nationality
                    </label>
                    <select
                      id="nationality"
                      name="nationality"
                      className="edit-profile-modal__select"
                      value={nationality}
                      onChange={(e) => setNationality(e.target.value)}
                    >
                      <option value="">Select nationality</option>
                      {nationalityData.map((entry, index) => (
                        <option key={`${entry.nationality}-${index}`} value={entry.nationality}>
                          {entry.nationality}
                        </option>
                      ))}
                    </select>
                    {errorData.nationality ? (
                      <span className="edit-profile-modal__error">{errorData.nationality}</span>
                    ) : null}
                  </div>

                  <div>
                    <label htmlFor="gender" className="edit-profile-modal__label">
                      Gender
                    </label>
                    <select
                      id="gender"
                      name="gender"
                      className="edit-profile-modal__select"
                      value={gender}
                      onChange={(e) => setGender(e.target.value)}
                    >
                      <option value="">Select gender</option>
                      <option value="M">Male</option>
                      <option value="F">Female</option>
                    </select>
                    {errorData.gender ? (
                      <span className="edit-profile-modal__error">{errorData.gender}</span>
                    ) : null}
                  </div>
                </div>

                <div className="edit-profile-modal__field edit-profile-modal__date">
                  <label htmlFor="dob" className="edit-profile-modal__label">
                    Date of Birth
                  </label>
                  <DatePicker
                    id="dob"
                    name="dob"
                    value={dobValue}
                    onChange={setDobValue}
                    format="MM/dd/yyyy"
                    maxDate={addDays(new Date(), -6500)}
                    clearIcon={null}
                    calendarIcon={<i className="fas fa-calendar-alt" aria-hidden="true" />}
                  />
                  {errorData.dob ? (
                    <span className="edit-profile-modal__error">{errorData.dob}</span>
                  ) : null}
                </div>

                <div className="edit-profile-modal__actions">
                  <button type="button" className="edit-profile-modal__cancel" onClick={closeModal}>
                    Cancel
                  </button>
                  <button type="submit" className="edit-profile-modal__submit" disabled={loading}>
                    <i className="fas fa-check" aria-hidden="true" />
                    Save Changes
                  </button>
                </div>
              </form>
            </Fragment>
          )}
        </div>
      </div>
    </div>
  );
};

export default EditProfile;
